import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BarChart3, Crown, TrendingUp, Target,
  MousePointerClick, BookOpen, Star, Sparkles, Zap, Clock,
  Users, Flame, Compass, ArrowUpRight, ChevronRight
} from 'lucide-react';

const ScoreBar = ({ label, score, icon: Icon }) => {
  const color = score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = score >= 80 ? 'text-emerald-400' : score >= 60 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="space-y-1.5" data-testid={`score-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${textColor}`} />
          <span className="text-sm text-slate-300">{label}</span>
        </div>
        <span className={`text-sm font-bold ${textColor}`}>{score}/100</span>
      </div>
      <div className="w-full h-2 rounded-full bg-slate-700/50">
        <div
          className={`h-2 rounded-full transition-all duration-1000 ease-out ${color}`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
};

const ScoresSection = ({ scores }) => (
  <Card className="bg-slate-800/30 border-slate-700/50">
    <CardContent className="p-6">
      <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-violet-400" />
        Content Scores
      </h2>
      <div className="space-y-4">
        <ScoreBar label="Hook Strength" score={scores.hookStrength} icon={Zap} />
        <ScoreBar label="SEO Score" score={scores.seoScore} icon={Target} />
        <ScoreBar label="Click-Through Potential" score={scores.clickThroughPotential} icon={MousePointerClick} />
        <ScoreBar label="Readability" score={scores.readability} icon={BookOpen} />
        <div className="pt-2 border-t border-slate-700/50">
          <ScoreBar label="Overall Score" score={scores.overall} icon={Star} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const TitleVariations = ({ titles }) => (
  <Card className="bg-slate-800/30 border-slate-700/50">
    <CardContent className="p-6">
      <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-cyan-400" />
        Rewritten Title Variations
      </h2>
      <div className="space-y-2">
        {titles.map((t, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/40">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 text-violet-400 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
            <span className="text-white text-sm">{t}</span>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const ImprovementsGrid = ({ description, hook }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
    <Card className="bg-slate-800/30 border-slate-700/50">
      <CardContent className="p-6">
        <h3 className="text-sm font-bold text-emerald-400 mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" /> Improved Description
        </h3>
        <p className="text-slate-300 text-sm leading-relaxed">{description}</p>
      </CardContent>
    </Card>
    <Card className="bg-slate-800/30 border-slate-700/50">
      <CardContent className="p-6">
        <h3 className="text-sm font-bold text-amber-400 mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4" /> Better Hook
        </h3>
        <p className="text-slate-300 text-sm leading-relaxed">{hook}</p>
      </CardContent>
    </Card>
  </div>
);

const CtaSection = ({ cta }) => (
  <Card className="bg-slate-800/30 border-slate-700/50">
    <CardContent className="p-6">
      <h3 className="text-sm font-bold text-violet-400 mb-3 flex items-center gap-2">
        <ArrowUpRight className="w-4 h-4" /> CTA Recommendation
      </h3>
      <p className="text-slate-300 text-sm leading-relaxed">{cta}</p>
    </CardContent>
  </Card>
);

const MaxInsightCard = ({ icon: Icon, title, content }) => (
  <Card className="bg-slate-800/30 border-cyan-500/20">
    <CardContent className="p-5">
      <h3 className="text-sm font-bold text-cyan-400 mb-2 flex items-center gap-2">
        <Icon className="w-4 h-4" /> {title}
      </h3>
      <p className="text-slate-300 text-sm">{content}</p>
    </CardContent>
  </Card>
);

const TopicBadge = ({ topic }) => (
  <Badge className="bg-cyan-500/10 text-cyan-300 border-cyan-500/30 px-3 py-1">
    <ChevronRight className="w-3 h-3 mr-1" /> {topic}
  </Badge>
);

const MaxExtrasSection = ({ maxExtras }) => {
  const topics = maxExtras.relatedGrowingTopics || [];

  return (
    <div className="space-y-5" data-testid="analyzer-max-extras">
      <div className="flex items-center gap-2 pt-2">
        <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 px-3 py-1">
          <Crown className="w-3.5 h-3.5 mr-1" /> MAX INSIGHTS
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <MaxInsightCard icon={Flame} title="Viral Potential" content={maxExtras.viralPotential} />
        <MaxInsightCard icon={Clock} title="Retention Prediction" content={maxExtras.retentionPrediction} />
        <MaxInsightCard icon={Clock} title="Best Posting Time" content={maxExtras.bestPostingTime} />
        <MaxInsightCard icon={Users} title="Audience Profile" content={maxExtras.audienceProfile} />
      </div>

      <MaxInsightCard icon={Target} title="Competitor Gap Analysis" content={maxExtras.competitorGapAnalysis} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <MaxInsightCard icon={TrendingUp} title="Trending Analysis" content={maxExtras.trendingAnalysis} />
        <MaxInsightCard icon={Compass} title="Best Angle Suggestion" content={maxExtras.bestAngleSuggestion} />
      </div>

      {topics.length > 0 && (
        <Card className="bg-slate-800/30 border-cyan-500/20">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-cyan-400 mb-3 flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4" /> Related Growing Topics
            </h3>
            <div className="flex flex-wrap gap-2">
              {topics.map((topic, i) => (
                <TopicBadge key={i} topic={topic} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export { ScoresSection, TitleVariations, ImprovementsGrid, CtaSection, MaxExtrasSection };
